import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  AddNoticeToListMutation,
  AddNoticeToListMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { BasicSignal, getSignalTypes } from "lib/types/models";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateCachedNotices } from "../utils";

const addNoticeToListMutation = graphql(`
  mutation addNoticeToList($input: NoticeListEntryInput!) {
    addNoticeToList(input: $input) {
      id
      name
    }
  }
`);

/** Where the notice was saved from; the list itself comes from the response. */
type TrackingContext = {
  contextSource: string;
  signals: BasicSignal[];
  recordStage: string;
};

export function useAddNoticeToList(
  { contextSource, signals, recordStage }: TrackingContext,
  options?: UseMutationOptions<
    AddNoticeToListMutation,
    unknown,
    AddNoticeToListMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();
  return useGraphQlMutation(addNoticeToListMutation, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      await queryClient.invalidateQueries(["noticeLists"]);
      const list = data.addNoticeToList;
      const signalTypes = getSignalTypes(signals);

      logEvent(EventNames.recordAddedToList, {
        "List name": list.name,
        "List id": list.id,
        "Data type": EventDataTypes.recordList,
        "Context source": contextSource,
        Stage: recordStage,
        "Signal types": signalTypes ? Object.keys(signalTypes) : [],
        Signals: signalTypes,
      });

      updateCachedNotices({
        queryClient,
        lookup: { type: "guid", guid: vars.input.noticeGuid },
        updateNoticesRow: (r) => ({
          ...r,
          lists: [...r.lists, { id: list.id, name: list.name }],
        }),
      });

      options?.onSuccess?.(data, vars, ctx);
    },
  });
}
